import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { ChatMessage, AnalysisReportData } from '@models/index'
import { omapApi } from '@services/omapApi'
import type { RootState } from '../store'
import type { NarrativeStatusByField } from '../../types/narrativeRefresh'

export interface Conversation {
  id: string
  companyId: string // Link to company
  title: string
  createdAt: string
  updatedAt: string
  messages: ChatMessage[]
  // Authoritative count from backend list endpoint (chat_logs + workflow_state
  // quick_responses). Prefer this over messages.length, which is 0 until the
  // conversation is hydrated.
  messageCount?: number
  strategyWorkspaceData?: any // MECE table, strategy map, graph data
  analysisReportData?: AnalysisReportData // Text reports, coverage attestation, strategic objectives
  oracleInsightsData?: any // Oracle inference results (persisted via workflow_state)
  currentMode?: 'quick' | 'deep' | 'comprehensive'
  workflowState?: any // For Comprehensive Mode gates
}

interface ConversationsState {
  conversations: Conversation[]
  activeConversationId: string | null
  loading: boolean
}

const initialState: ConversationsState = {
  conversations: [],
  activeConversationId: null,
  loading: false,
}

// Async Thunks
export const deleteConversationAsync = createAsyncThunk(
  'conversations/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const response = await omapApi.deleteConversation(conversationId)
      return { conversationId, response }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete conversation')
    }
  }
)

export const renameConversationAsync = createAsyncThunk(
  'conversations/renameConversation',
  async ({ conversationId, newTitle }: { conversationId: string; newTitle: string }, { rejectWithValue }) => {
    try {
      const response = await omapApi.renameConversation(conversationId, newTitle)
      return { conversationId, newTitle: response.new_title }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to rename conversation')
    }
  }
)

export const updateWorkspaceAsync = createAsyncThunk(
  'conversations/updateWorkspace',
  async (
    { conversationId, updates }: { conversationId: string; updates: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await omapApi.updateWorkspace(conversationId, updates)

      if (response.status === 'validation_error') {
        return rejectWithValue(response.validation_errors.join(', '))
      }

      return { conversationId, updatedWorkspace: response.updated_workspace }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || error.message)
    }
  }
)

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    createConversation: (state, action: PayloadAction<{ title?: string; companyId: string }>) => {
      const newConversation: Conversation = {
        id: `conv_${Date.now()}`,
        companyId: action.payload.companyId,
        title: action.payload.title || 'New Conversation',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [],
      }
      state.conversations.unshift(newConversation) // Add to beginning
      state.activeConversationId = newConversation.id
    },

    setActiveConversation: (state, action: PayloadAction<string>) => {
      state.activeConversationId = action.payload
    },

    addMessageToConversation: (state, action: PayloadAction<{ conversationId: string; message: ChatMessage }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        conv.messages.push(action.payload.message)
        conv.messageCount = (conv.messageCount ?? 0) + 1
        conv.updatedAt = new Date().toISOString()
      }
    },

    updateConversationTitle: (state, action: PayloadAction<{ conversationId: string; title: string }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        conv.title = action.payload.title
        conv.updatedAt = new Date().toISOString()
      }
    },

    updateStrategyWorkspace: (state, action: PayloadAction<{ conversationId: string; data: any }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        // Merge new data with existing data to preserve comprehensiveGate and other fields
        conv.strategyWorkspaceData = {
          ...conv.strategyWorkspaceData,
          ...action.payload.data
        }
        conv.updatedAt = new Date().toISOString()
      }
    },

    updateAnalysisReport: (state, action: PayloadAction<{ conversationId: string; data: any }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        // Phase 4.1: Merge instead of replace to preserve existing data (e.g., quick insights)
        conv.analysisReportData = {
          ...conv.analysisReportData,  // Preserve existing data
          ...action.payload.data        // Merge new data
        }
        conv.updatedAt = new Date().toISOString()
      }
    },

    updateWorkflowState: (state, action: PayloadAction<{ conversationId: string; workflowState: any }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        conv.workflowState = action.payload.workflowState
        conv.updatedAt = new Date().toISOString()
      }
    },

    triggerGateAdvancement: (state, action: PayloadAction<{
      conversationId: string
      gate: 1 | 2
      payload: any
      modifications?: any
    }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId)
      if (conv) {
        conv.workflowState = {
          ...conv.workflowState,
          advancement_trigger: {
            gate: action.payload.gate,
            payload: action.payload.payload,
            modifications: action.payload.modifications,
            timestamp: Date.now(),
          }
        }
      }
    },


    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Bulk load conversations from backend (for persistence restoration)
    loadConversations: (state, action: PayloadAction<Conversation[]>) => {
      state.conversations = action.payload
      // Always select the first conversation (handles company switch correctly)
      state.activeConversationId = action.payload.length > 0 ? action.payload[0].id : null
    },

    // Hydrate single conversation with full data (messages, workspace, etc.)
    hydrateConversation: (state, action: PayloadAction<Conversation>) => {
      const existingIdx = state.conversations.findIndex(c => c.id === action.payload.id)
      if (existingIdx >= 0) {
        // Replace existing conversation with hydrated data
        state.conversations[existingIdx] = action.payload
      } else {
        // Add new conversation
        state.conversations.unshift(action.payload)
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Delete conversation
      .addCase(deleteConversationAsync.pending, (state) => {
        state.loading = true
      })
      .addCase(deleteConversationAsync.fulfilled, (state, action) => {
        state.loading = false
        const conversationId = action.payload.conversationId
        // Remove conversation from state
        state.conversations = state.conversations.filter(c => c.id !== conversationId)
        // Update active conversation if deleted
        if (state.activeConversationId === conversationId) {
          state.activeConversationId = state.conversations[0]?.id || null
        }
      })
      .addCase(deleteConversationAsync.rejected, (state, action) => {
        state.loading = false
        console.error('Failed to delete conversation:', action.payload)
      })
      // Rename conversation
      .addCase(renameConversationAsync.pending, (state) => {
        state.loading = true
      })
      .addCase(renameConversationAsync.fulfilled, (state, action) => {
        state.loading = false
        const { conversationId, newTitle } = action.payload
        const conv = state.conversations.find(c => c.id === conversationId)
        if (conv) {
          conv.title = newTitle
          conv.updatedAt = new Date().toISOString()
        }
      })
      .addCase(renameConversationAsync.rejected, (state, action) => {
        state.loading = false
        console.error('Failed to rename conversation:', action.payload)
      })
      // Update workspace
      .addCase(updateWorkspaceAsync.pending, (state) => {
        state.loading = true
      })
      .addCase(updateWorkspaceAsync.fulfilled, (state, action) => {
        state.loading = false
        const conv = state.conversations.find(c => c.id === action.payload.conversationId)
        if (conv) {
          // Extract stale flags from response (snake_case from backend)
          const strategyMapStale = action.payload.updatedWorkspace.strategy_map_stale || false
          const strategyMapStaleReason = action.payload.updatedWorkspace.strategy_map_stale_reason || null

          // Check if workflow is in gate mode (has comprehensiveGate) vs completed mode
          if (conv.strategyWorkspaceData?.comprehensiveGate) {
            // In-progress workflow: update inside comprehensiveGate.data
            conv.strategyWorkspaceData = {
              ...conv.strategyWorkspaceData,
              strategyMapStale,
              strategyMapStaleReason,
              comprehensiveGate: {
                ...conv.strategyWorkspaceData.comprehensiveGate,
                data: {
                  ...conv.strategyWorkspaceData.comprehensiveGate.data,
                  mece_table: action.payload.updatedWorkspace.mece_table,
                  strategy_map: action.payload.updatedWorkspace.strategy_map
                }
              }
            }
          } else {
            // Completed workflow: update meceTable/strategyMap directly on workspace
            conv.strategyWorkspaceData = {
              ...conv.strategyWorkspaceData,
              meceTable: action.payload.updatedWorkspace.mece_table,
              strategyMap: action.payload.updatedWorkspace.strategy_map,
              strategyMapStale,
              strategyMapStaleReason
            }
          }
          conv.updatedAt = new Date().toISOString()
        }
      })
      .addCase(updateWorkspaceAsync.rejected, (state, action) => {
        state.loading = false
        console.error('Workspace update failed:', action.payload)
      })
  },
})

export const {
  createConversation,
  setActiveConversation,
  addMessageToConversation,
  updateConversationTitle,
  updateStrategyWorkspace,
  updateAnalysisReport,
  updateWorkflowState,
  triggerGateAdvancement,
  setLoading,
  loadConversations,
  hydrateConversation,
} = conversationsSlice.actions

export default conversationsSlice.reducer

// ---------------------------------------------------------------------------
// Canvas scorecard thunks
// ---------------------------------------------------------------------------

/**
 * Mutates a single TacticMetric's target inside the active conversation's
 * strategyMap. Updates local Redux state immediately for responsive UX,
 * then persists via updateWorkspaceAsync. The backend's partial-update
 * mode accepts target writes against legacy maps that would otherwise
 * fail full validation, so this call is reliable rather than best-effort.
 */
export const updateMetricTarget = createAsyncThunk<
  void,
  { metricId: string; value: string },
  { state: RootState }
>('conversations/updateMetricTarget', async ({ metricId, value }, { getState, dispatch }) => {
  const state = getState()
  const convId = state.conversations.activeConversationId
  if (!convId) return
  const conv = state.conversations.conversations.find(c => c.id === convId)
  const map = conv?.strategyWorkspaceData?.strategyMap
  if (!map) return

  // Deep-clone the strategyMap, then mutate the target on the matching metric.
  const next = JSON.parse(JSON.stringify(map))

  const writeTarget = (metric: any): boolean => {
    if (metric.id !== metricId) return false
    const trimmed = value.trim()
    if (!trimmed) {
      delete metric.target
    } else {
      metric.target = { ...(metric.target ?? {}), value: trimmed }
    }
    return true
  }

  let written = false
  outer: for (const pillar of next.pillars ?? []) {
    for (const tactic of pillar.tactics ?? []) {
      for (const metric of tactic.metrics ?? []) {
        if (writeTarget(metric)) {
          written = true
          break outer
        }
      }
    }
  }
  if (!written) return

  // Optimistic local update — input feels responsive while the API call
  // is in flight. updateWorkspaceAsync.fulfilled will overwrite local
  // state with the server response (which now also has the new target).
  dispatch(updateStrategyWorkspace({ conversationId: convId, data: { strategyMap: next } }))

  await dispatch(updateWorkspaceAsync({
    conversationId: convId,
    updates: { strategy_map: next },
  }))
})

/**
 * Phase 2-E: Regenerate the Analysis Report from the current workspace state.
 * Calls the backend `/strategy_plan/regenerate` endpoint and dispatches
 * `updateAnalysisReport` so existing analysisReportData fields (e.g., quick
 * insights) are preserved via the reducer's merge behavior.
 */
export const regenerateAnalysisReport = createAsyncThunk<
  {
    fullReport: string
    generatedAt: string
    narrativeRefreshStatus?: 'refreshed' | 'partial' | 'skipped'
    narrativeStatusByField?: NarrativeStatusByField
  },
  { conversationId: string },
  { rejectValue: string }
>(
  'conversations/regenerateAnalysisReport',
  async ({ conversationId }, { dispatch, rejectWithValue }) => {
    try {
      const result = await omapApi.regenerateStrategyPlan(conversationId)
      dispatch(updateAnalysisReport({
        conversationId,
        data: {
          fullReport: result.fullReport,
          strategyPlanGeneratedAt: result.generatedAt,
        },
      }))
      return {
        fullReport: result.fullReport,
        generatedAt: result.generatedAt,
        narrativeRefreshStatus: result.narrativeRefreshStatus,
        narrativeStatusByField: result.narrativeStatusByField,
      }
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Regenerate failed')
    }
  }
)

/**
 * Phase 5: dispatch refresh-narratives. Mirrors regenerateAnalysisReport shape;
 * additionally clears narrativeStaleness.isStale on fulfilled (since narratives
 * just refreshed, they are no longer stale).
 */
export const refreshNarratives = createAsyncThunk<
  {
    fullReport: string
    generatedAt: string
    narrativeRefreshStatus?: 'refreshed' | 'partial' | 'skipped'
    narrativeStatusByField?: NarrativeStatusByField
    narrativesRefreshedAt: string
  },
  { conversationId: string },
  { rejectValue: string }
>(
  'conversations/refreshNarratives',
  async ({ conversationId }, { dispatch, rejectWithValue }) => {
    try {
      const result = await omapApi.refreshNarratives(conversationId)
      dispatch(updateAnalysisReport({
        conversationId,
        data: {
          fullReport: result.fullReport,
          strategyPlanGeneratedAt: result.generatedAt,
          narrativeStaleness: {
            isStale: false,
            inputsChangedAt: null,
            narrativesRefreshedAt: result.narrativesRefreshedAt,
          },
        },
      }))
      return {
        fullReport: result.fullReport,
        generatedAt: result.generatedAt,
        narrativeRefreshStatus: result.narrativeRefreshStatus,
        narrativeStatusByField: result.narrativeStatusByField,
        narrativesRefreshedAt: result.narrativesRefreshedAt,
      }
    } catch (err: any) {
      return rejectWithValue(err?.message ?? 'Refresh failed')
    }
  }
)
